import { useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ampli } from '../../../../ampli';
import { Button } from '../../../../components/common/buttons/Button';
import { MainHead } from '../../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../../components/common/loading/AnimateLoading';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useDeletePokeToUserOwnTractor } from '../../../../hooks/api/product/mutations/useDeletePokeToUserOwnTractor.mutation';
import { usePostPokeToUserOwnTractor } from '../../../../hooks/api/product/mutations/usePostPokeToUserOwnTractor.mutation';
import { useGetUsersOwnTractorById } from '../../../../hooks/api/product/queries/useGetUsersOwnTractorById.query';
import { useGetDownloadAppModalHeight } from '../../../../hooks/modal/useGetDownloadAppModalHeight';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';
import { IMachine } from '../../../../interfaces/Machine.interfaces';
import { browserStorage } from '../../../../utils/browserStorage.util';
import { formatPrice } from '../../../../utils/formatter/formatPrice.util';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { getRegionByDepth, RegionDepth } from '../../../../utils/getter/getShortAddress.util';
import { NumberKeyboard } from './components/NumberKeyboard';
import { useAdvertisementConsent } from '../../../../hooks/user/useAdvertisementConsent';
import { usePostUserActivityTracking } from '../../../../hooks/api/user/mutations/usePostUserActivityTracking.mutation';
import { usePatchFlarelaneUserTags } from '../../../../hooks/api/flarelane/usePatchFlarelaneUserTags';
import { useQueryClient } from '@tanstack/react-query';

interface IPurposeInfo {
  title: string;
  content: string;
}

const PURPOSE_INFO: Record<string, IPurposeInfo> = {
  DEFAULT: {
    title: '',
    content: '',
  },
  MY_TRACTOR: {
    title: '내가 보유한 트랙터예요!',
    content: '다른 트랙터 구경하고\n콕! 콕! 찔러보기 해보세요!',
  },
  POKED_WITH_PRICE: {
    title: '',
    content: '내가 관심을 표현한 가격 💰',
  },
  POKED_WITHOUT_PRICE: {
    title: '이미 찔러보기 완료 했어요!',
    content: '혹시 가격 제안도 해보고 싶다면\n찔러보기 취소 후에 다시 신청해주세요 👇🏻',
  },
  NOT_POKED: {
    title: '',
    content: '혹시 몰라요! 제안하신 가격이\n보유자 마음에 들면\n직거래 매물로 등록될 수도 있어요 🙂',
  },
};

export const TractorPokingDetailPage = () => {
  const queryClient = useQueryClient();
  const { productId } = useParams();
  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { userProfile } = useGetUserProfile();

  const PRICE_KEY = 'price';
  const MANWON_CONVERSION_RATE = 10000;
  const TRACTOR_POKING_DETAIL_PAGE_TYPE = 'TDP';

  const downloadAppModalHeight = useGetDownloadAppModalHeight();
  const nonContentHeight = 156 + downloadAppModalHeight;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: userOwnProduct,
    isLoading: isGetUserOwnProductLoading,
    refetch: refetchPokingDetail,
  } = useGetUsersOwnTractorById({
    tractorId: productId!,
  });

  const [isPokeSuccessModalOpen, setIsPokeSuccessModalOpen] = useState<boolean>(false);
  const [isAdvertisementConsentModalOpen, setIsAdvertisementConsentModalOpen] = useState<boolean>(false);
  const [isPokeCancelSuccessModalOpen, setIsPokeCancelSuccessModalOpen] = useState<boolean>(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [isUserActivityUpdated, setIsUserActivityUpdated] = useState<boolean>(false);

  const updateAdConsent = useAdvertisementConsent('TDP');
  const { mutate: postPokeToUserOwnTractor } = usePostPokeToUserOwnTractor();
  const { mutate: deletePokeToUserOwnTractor } = useDeletePokeToUserOwnTractor();
  const { mutate: postUserActivityTracking } = usePostUserActivityTracking();
  const { mutate: patchFlarelaneUserTags } = usePatchFlarelaneUserTags();

  const hasAgreedToAdvertisementTerms = userProfile?.advertisementTermsAgreement;

  const purposeInfo = useMemo<IPurposeInfo>(() => {
    if (!userOwnProduct) {
      return PURPOSE_INFO.DEFAULT;
    }

    const { isMyTractor, isTractorPokedByMe } = userOwnProduct;

    if (isMyTractor) {
      return PURPOSE_INFO.MY_TRACTOR;
    }

    if (isTractorPokedByMe) {
      return userOwnProduct.suggestedPrice ? PURPOSE_INFO.POKED_WITH_PRICE : PURPOSE_INFO.POKED_WITHOUT_PRICE;
    }
    if (!isMyTractor && !isTractorPokedByMe) {
      return PURPOSE_INFO.NOT_POKED;
    }

    return PURPOSE_INFO.DEFAULT;
  }, [userOwnProduct]);

  const pokingDetailTitle = useMemo(() => {
    if (!userOwnProduct) {
      return '';
    }
    return getOpenMarketProductTitle({
      brandName: userOwnProduct.brand?.name || '',
      machine: userOwnProduct.machine as IMachine,
    });
  }, [userOwnProduct]);

  const {
    register,
    control,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange', defaultValues: { [PRICE_KEY]: '' } });

  const handlePokeClick = () => {
    if (!userOwnProduct || !userOwnProduct.id) {
      return;
    }

    ampli.pvmClickPoke({
      title: pokingDetailTitle,
      type: 'negotiable',
      price: Number(getValues(PRICE_KEY)),
    });

    if (!isUserLoggedIn) {
      setIsLoginModalOpen(true);
      return;
    }

    if (!hasAgreedToAdvertisementTerms) {
      setIsAdvertisementConsentModalOpen(true);
      return;
    }

    executePoke();
  };

  const handleSavePageAndRedirectToLogin = () => {
    const price = getValues(PRICE_KEY);
    ampli.pvmClickLoginForPoke();
    setIsLoginModalOpen(false);
    // TODO: 필요시 기능화
    browserStorage.setItem({
      storageType: 'sessionStorage',
      itemKey: 'storedPageForRedirection',
      value: `${location.pathname}?price=${price}`,
    });
    navigate(pageInfo.loginPage.path);
  };

  const handlePokeWithAgreement = () => {
    updateAdConsent({
      isAgreeing: true,
      onSuccess: () => {
        setIsAdvertisementConsentModalOpen(false);
        executePoke();
      },
    });
  };

  const executePoke = () => {
    if (!userOwnProduct) {
      return;
    }

    ReactGA.event('pvm_poke_own_tractor', {
      isModal: false,
    });

    const convertedPrice = Number(getValues(PRICE_KEY)) * MANWON_CONVERSION_RATE;
    postPokeToUserOwnTractor(
      {
        tractorId: userOwnProduct.id,
        reqBody: {
          suggestedPrice: convertedPrice,
        },
      },
      {
        onSuccess: () => {
          setIsPokeSuccessModalOpen(true);
          ampli.pvmClickPokeCompleted({
            user_id: userProfile?.id || '',
            title: pokingDetailTitle,
            ps: userOwnProduct.machine?.engineOutputInPs,
            manufacturedYear: userOwnProduct.manufacturedYear,
            operationTime: userOwnProduct.operationTime,
            type: 'negotiable',
            price: Number(getValues(PRICE_KEY)),
          });
          ReactGA.event({
            category: 'poke',
            action: 'completed',
            label: pokingDetailTitle,
            value: Number(getValues(PRICE_KEY)),
          });
        },
      },
    );
  };

  const handlePokeCancelClick = () => {
    if (!userOwnProduct || !userOwnProduct.id) {
      return;
    }

    ampli.pvmClickPokeCancel({
      title: pokingDetailTitle,
    });

    deletePokeToUserOwnTractor(
      {
        tractorId: userOwnProduct.id,
      },
      {
        onSuccess: () => {
          setIsPokeCancelSuccessModalOpen(true);
        },
      },
    );
  };

  const renderFixedBottomButton = () => {
    if (!userOwnProduct) {
      return null;
    }

    if (userOwnProduct.isMyTractor) {
      return (
        <Button
          borderType={'square'}
          color={'filledGreyStrong'}
          size={'lg'}
          text={{ size: 'md', content: '보유 트랙터 관리하러 가기' }}
          isDisabled={!isValid}
          onClick={() => {
            ampli.pvmClickOwnTractorManage();
            navigate(pageInfo.myInfoOwnTractorListPage.path);
          }}
        />
      );
    }

    if (userOwnProduct.isTractorPokedByMe) {
      return (
        <Button
          borderType={'square'}
          color={'filledGreyStrong'}
          size={'lg'}
          text={{ size: 'md', content: '찔러보기 취소' }}
          onClick={handlePokeCancelClick}
        />
      );
    }

    if (!userOwnProduct.isTractorPokedByMe && !userOwnProduct.isMyTractor) {
      return (
        <Button
          borderType={'square'}
          color={'filledPrimaryStrong'}
          size={'lg'}
          text={{ size: 'md', content: '이 가격으로 찔러보기' }}
          isDisabled={!isValid}
          onClick={handlePokeClick}
        />
      );
    }

    return null;
  };

  const formatPriceInputValue = (value: string) => {
    if (!value) return '';

    return `${formatPrice(Number(value) * MANWON_CONVERSION_RATE)}원`;
  };

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true, directlyPassedURL: pageInfo.tractorPokingListPage.path }}
        headerTitle={'트랙터 찔러보기'}
      />
    ),
    fixedFooter: (
      <FixedFooterWrapper>
        <BlurredSection />
        {renderFixedBottomButton()}
      </FixedFooterWrapper>
    ),
    fixedHeaderHeight: 48,
    fixedFooterHeight: 116,
    shouldShowFooter: false,
  });

  useEffect(() => {
    if (!isUserActivityUpdated) {
      postUserActivityTracking('pokeDetailPageVisitedCount', {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['user_activity_tracking', 'TDP'] });
        },
      });
      setIsUserActivityUpdated(true);
    }
  }, [postUserActivityTracking, isUserActivityUpdated, queryClient]);

  useEffect(() => {
    if (userProfile) {
      patchFlarelaneUserTags({ lastVisitedTDP: new Date().toISOString() });
    }
  }, [userProfile, patchFlarelaneUserTags]);

  useEffect(() => {
    if (userOwnProduct && userOwnProduct.isTractorPokedByMe) {
      const convertedPrice = userOwnProduct.suggestedPrice / MANWON_CONVERSION_RATE;
      setValue(PRICE_KEY, String(convertedPrice));
    }
  }, [userOwnProduct]);

  useEffect(() => {
    if (userOwnProduct && !userOwnProduct.isTractorPokedByMe && searchParams.has(PRICE_KEY)) {
      const price = searchParams.get(PRICE_KEY);
      setValue(PRICE_KEY, String(price), { shouldValidate: true });
    }
  }, [searchParams, userOwnProduct]);

  if (isGetUserOwnProductLoading) return <AnimateLoading />;

  if (userOwnProduct) {
    return (
      <>
        <Helmet>
          <title>{`${pokingDetailTitle} - Pluva Market`}</title>
        </Helmet>

        <Container nonContentHeight={nonContentHeight}>
          <TractorOverviewSectionWrapper>
            <TractorInfo>
              <TractorTitle>{pokingDetailTitle}</TractorTitle>
              <MetaDataContainer>
                <MetaData>{getRegionByDepth(userOwnProduct.region, RegionDepth.DISTRICT)}</MetaData>
                <MetaData>{'·'}</MetaData>
                <MetaData>{`${String(userOwnProduct?.manufacturedYear).slice(0, 4)}년식`}</MetaData>
                <MetaData>{'·'}</MetaData>
                <MetaData>{`${Number(userOwnProduct?.operationTime).toLocaleString()}시간`}</MetaData>
              </MetaDataContainer>
            </TractorInfo>
          </TractorOverviewSectionWrapper>

          <PurposeDescriptionSection>
            <PurposeContentWrapper>
              {purposeInfo.title && purposeInfo.title.length > 0 ? (
                <PurposeTitle>{purposeInfo.title}</PurposeTitle>
              ) : null}
              {purposeInfo.content && purposeInfo.content.length > 0 ? (
                <PurposeContent>{purposeInfo.content}</PurposeContent>
              ) : null}
              {!userOwnProduct?.isMyTractor ? (
                <Controller
                  name={PRICE_KEY}
                  control={control}
                  render={({ field }) => (
                    <>
                      {!userOwnProduct.isTractorPokedByMe ||
                      (userOwnProduct.isTractorPokedByMe && userOwnProduct.suggestedPrice) ? (
                        <PriceInputWrapper>
                          <PriceInput
                            {...field}
                            readOnly
                            placeholder={'제안가 입력 (만원 단위)'}
                            hasError={!!errors.price?.message}
                            value={formatPriceInputValue(field.value)}
                            {...register(PRICE_KEY, {
                              required: true,
                              min: { value: 100, message: '너무 낮은 금액으로는 제안할 수 없어요 😥' },
                              max: { value: 50000, message: '금액이 너무 높은것 같아요 🙄' },
                            })}
                          />
                          {errors.price && (
                            <PriceInputErrorMessageWrapper>
                              <PriceInputErrorMessage>{errors.price.message?.toString()}</PriceInputErrorMessage>
                            </PriceInputErrorMessageWrapper>
                          )}
                        </PriceInputWrapper>
                      ) : null}

                      {!userOwnProduct?.isTractorPokedByMe ? (
                        <NumberKeyboard
                          handleKeyPress={(pressedNumber) => {
                            const currentPrice = field.value;
                            if (!currentPrice) {
                              setValue('price', pressedNumber, { shouldValidate: true });
                              return;
                            }
                            field.onChange(currentPrice + pressedNumber);
                          }}
                          handleDelete={() => {
                            field.onChange(field.value.slice(0, -1));
                          }}
                        />
                      ) : null}
                    </>
                  )}
                />
              ) : null}
            </PurposeContentWrapper>
            {userOwnProduct?.isTractorPokedByMe && (
              <PokedByMeDescriptionSection>
                <PokedByMeDescription>{'* 트랙터당 한번의 제안만 가능해요.'}</PokedByMeDescription>
                <PokedByMeDescription>{'* 재신청을 원하시면 찔러보기 취소 후 다시 신청해주세요'}</PokedByMeDescription>
              </PokedByMeDescriptionSection>
            )}
          </PurposeDescriptionSection>
        </Container>

        <PopupConfirmModal
          isShow={isPokeSuccessModalOpen}
          hasCloseButton={false}
          title={'👌🏻 요청이 완료되었어요!'}
          description={'찔러보기 하신 트랙터가 판매중으로\n전환되면 알람을 보내드릴게요!'}
          buttonArray={[
            {
              text: '찔러본 트랙터 보러가기',
              onClick: () => navigate(pageInfo.myInfoPokeListPage.path),
              color: 'blue',
            },
            {
              text: '돌아가기',
              onClick: () => navigate(pageInfo.tractorPokingListPage.path),
              color: 'gray',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isPokeCancelSuccessModalOpen}
          hasCloseButton={false}
          title={'🙂 취소가 완료 되었어요'}
          description={'요청하신 찔러보기가 취소되었어요'}
          buttonArray={[
            {
              text: '확인',
              onClick: () => {
                refetchPokingDetail();
                navigate(pageInfo.tractorPokingListPage.path);
              },
              color: 'blue',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isAdvertisementConsentModalOpen}
          hasCloseButton={false}
          closeModal={() => setIsAdvertisementConsentModalOpen(false)}
          title={'🔔 알람 동의를 해주세요!'}
          description={'찔러보기 하신 매물의 판매 알람을\n받으시려면 알람 동의가 필요해요!'}
          buttonArray={[
            {
              text: '동의하고 알람 받기',
              onClick: handlePokeWithAgreement,
              color: 'blue',
            },
            {
              text: '닫기',
              onClick: () => {
                ampli.pvmClickSetAlarmModalClose({
                  page_type: TRACTOR_POKING_DETAIL_PAGE_TYPE,
                });
                setIsAdvertisementConsentModalOpen(false);
              },
              color: 'gray',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isLoginModalOpen}
          hasCloseButton={false}
          closeModal={() => setIsLoginModalOpen(false)}
          title={'로그인이 필요합니다.'}
          description={'로그인하러 가시겠습니까?'}
          buttonArray={[
            {
              text: '로그인',
              onClick: handleSavePageAndRedirectToLogin,
              color: 'blue',
            },
            {
              text: '닫기',
              onClick: () => setIsLoginModalOpen(false),
              color: 'gray',
            },
          ]}
        />
      </>
    );
  }
  return null;
};

const Container = styled.div<{ nonContentHeight: number }>`
  display: flex;
  flex-direction: column;
  height: ${(props) => `calc(100vh - ${props.nonContentHeight}px)`};
`;

const FixedFooterWrapper = styled.div`
  display: flex;
  padding: 0px 16px 32px 16px;
  background-color: white;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const TractorOverviewSectionWrapper = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;

  @media screen and (max-width: 340px) {
    display: none;
  }
`;

const TractorInfo = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const TractorTitle = styled.h3`
  color: var(--Light-Scheme-Color-primary-blue, #445eff);
  text-align: center;

  align-self: stretch;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const MetaDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const MetaData = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* System/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 13.6px */
`;

const PurposeDescriptionSection = styled.section`
  display: flex;
  padding: 0px 16px 16px 16px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex: 1 0 0;
  align-self: stretch;
`;

const PurposeContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const PurposeTitle = styled.div`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const PurposeContent = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;
const PriceInputWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
`;

const PriceInput = styled.input<{ hasError: boolean }>`
  display: flex;
  height: 53px;
  padding: 16px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  border-radius: var(--radius-large, 16px);
  border: 1px solid
    ${(props) =>
      props.hasError
        ? 'var(--Light-Scheme-Color-primary-red, #FF3049)'
        : 'var(--Light-Scheme-Monochrome-grey20, #ececf3)'};

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 26px */

  &::placeholder {
    color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);
  }
`;

const PriceInputErrorMessageWrapper = styled.div`
  position: absolute;
  bottom: -3px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const PriceInputErrorMessage = styled.p`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const PokedByMeDescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
  align-self: stretch;
`;

const PokedByMeDescription = styled.pre`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;
